import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Eye, EyeOff, Info, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTH_ERRORS = {
  401: "Invalid email or password. Please try again.",
  402: "Your account is locked. Please contact KYo support.",
  404: "No account found with this email address.",
  422: "Invalid credentials. Please check your details.",
  502: "Authentication service is currently unavailable. Please try again shortly.",
};

function getAuthError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  return AUTH_ERRORS[status] || detail || "Sign in failed. Please try again.";
}

/* ── Floating-label text input ── */
function FloatInput({ label, id, type = "text", value, onChange, required, placeholder }) {
  return (
    <div className="relative">
      <label
        htmlFor={id}
        className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none"
      >
        {label}
        {required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors placeholder:text-gray-300"
      />
    </div>
  );
}

/* ── Floating-label password input ── */
function PasswordInput({ label, id, value, onChange, required }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <label
        htmlFor={id}
        className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none"
      >
        {label}
        {required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 pr-10 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        tabIndex={-1}
      >
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

/* ── Success state ── */
function SuccessState({ email, onContinue }) {
  return (
    <div className="text-center py-4" data-testid="signin-success-screen">
      <div className="flex justify-center mb-5">
        <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
          <CheckCircle size={48} className="text-green-500" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome back!</h2>
      <p className="text-gray-500 text-sm mb-2">Signed in as</p>
      <p className="font-semibold text-gray-800 mb-8">{email}</p>
      <button
        onClick={onContinue}
        className="bg-[#ED2E24] hover:bg-[#c9231a] text-white font-semibold px-8 py-3 rounded-full transition-colors text-sm"
        data-testid="signin-continue-btn"
      >
        Go to My Dashboard
      </button>
    </div>
  );
}

export default function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password,
      });

      // Persist auth session
      localStorage.setItem(
        "kyo_auth",
        JSON.stringify({
          email: email.trim(),
          token: data.token ?? data.access_token ?? data.jwt ?? "",
          loginAt: new Date().toISOString(),
        })
      );
      setSuccess(true);
      toast.success("Signed in successfully!");
    } catch (err) {
      setError(getAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f2f2f2] flex flex-col" data-testid="signin-page">
      {/* Top bar */}
      <div className="pt-5 pb-2 px-6">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft size={16} />
          Back to KYo Group
        </button>
      </div>

      {/* Centred card */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div
          className="bg-white rounded-2xl shadow-lg w-full max-w-[460px] p-8 sm:p-10"
          data-testid="signin-card"
        >
          {success ? (
            <SuccessState email={email} onContinue={() => navigate("/dashboard")} />
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Welcome back</h1>
                <p className="text-gray-400 text-sm mt-1.5">Sign in to your KYo account</p>
              </div>

              {error && (
                <div
                  className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl flex items-start gap-2"
                  data-testid="signin-error"
                >
                  <Info size={15} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate data-testid="signin-form">
                <div className="space-y-5">
                  <FloatInput
                    label="Email" id="signin-email" type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="info@xyz.com"
                  />
                  <PasswordInput
                    label="Password" id="signin-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className="text-right -mt-2">
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-[12px] text-gray-400 hover:text-[#ED2E24] transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-7 w-full bg-[#ED2E24] hover:bg-[#c9231a] disabled:opacity-70 text-white font-semibold py-3.5 rounded-full transition-colors text-sm"
                  data-testid="signin-submit-btn"
                >
                  {loading ? "Signing in…" : "Sign in with KYo"}
                </button>
              </form>

              <p className="text-center mt-5 text-sm text-gray-500">
                Don't have an account?{" "}
                <button
                  onClick={() => navigate("/signup")}
                  className="text-[#ED2E24] hover:underline font-medium"
                  data-testid="goto-signup-link"
                >
                  Sign up
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
